/** Reusable coordinate picker: two `type="number"` inputs (Latitude, Longitude)
 *  with inline range validation, plus — when a Google Maps key is configured — a
 *  lazily-loaded draggable/clickable marker below them, two-way synced with the
 *  inputs.
 *
 *  Layout-agnostic: the parent controls width (mirrors RemoteAutocomplete). The
 *  component is fully controlled — it holds no coordinate state of its own; every
 *  edit (input, marker drag, or map click) flows out through `onChange`.
 *
 *  The map lives in a separate chunk (`React.lazy(() => import('./LatLngPickerMap'))`)
 *  so `@react-google-maps/api` is code-split off the editors' critical path — an
 *  editor that never renders a picker never loads the Maps library. When no key is
 *  configured the map inner is never imported and the component degrades to the two
 *  inputs plus a small "map unavailable" hint (mirrors the fleet VehiclesMap). */
import { Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import React, { Suspense, useId } from 'react';

import { config } from '@/config';

export interface LatLngValue {
  lat: number | null;
  lng: number | null;
}

export interface LatLngPickerProps {
  value: LatLngValue;
  onChange: (v: LatLngValue) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  /** Optional section header rendered above the inputs. Omit it when the parent
   *  already labels the section (bus-stop/activity "Location") to avoid a
   *  duplicate heading. */
  label?: string;
}

// Code-split boundary: importing this pulls @react-google-maps/api into its own
// chunk. Referenced only inside the `hasKey` branch below, so the no-key path
// never triggers the dynamic import.
const LatLngPickerMap = React.lazy(() => import('./LatLngPickerMap'));

const LAT_MIN = -90;
const LAT_MAX = 90;
const LNG_MIN = -180;
const LNG_MAX = 180;

/** Parse a text input into a coordinate. Blank → null; anything unparseable →
 *  null (the field then reads as empty, matching the "cleared" state). */
function parseCoord(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function rangeError(v: number | null, min: number, max: number): string | null {
  if (v === null) return null;
  if (v < min || v > max) return `Between ${min} and ${max}`;
  return null;
}

export function LatLngPicker({ value, onChange, disabled, size = 'small', label }: LatLngPickerProps) {
  const hasKey = Boolean(config.googleMapsApiKey);
  // Stable ids so the number inputs stay addressable across re-renders.
  const baseId = useId();

  const latError = rangeError(value.lat, LAT_MIN, LAT_MAX);
  const lngError = rangeError(value.lng, LNG_MIN, LNG_MAX);

  const latText = value.lat === null ? '' : String(value.lat);
  const lngText = value.lng === null ? '' : String(value.lng);

  return (
    <Stack spacing={1.5}>
      {label && (
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          id={`${baseId}-lat`}
          type="number"
          size={size}
          sx={{ width: 200 }}
          disabled={disabled}
          label="Latitude"
          value={latText}
          onChange={(e) => onChange({ lat: parseCoord(e.target.value), lng: value.lng })}
          error={Boolean(latError)}
          helperText={latError ?? undefined}
          inputProps={{ step: 'any', min: LAT_MIN, max: LAT_MAX }}
        />
        <TextField
          id={`${baseId}-lng`}
          type="number"
          size={size}
          sx={{ width: 200 }}
          disabled={disabled}
          label="Longitude"
          value={lngText}
          onChange={(e) => onChange({ lat: value.lat, lng: parseCoord(e.target.value) })}
          error={Boolean(lngError)}
          helperText={lngError ?? undefined}
          inputProps={{ step: 'any', min: LNG_MIN, max: LNG_MAX }}
        />
      </Box>

      {hasKey ? (
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
              <CircularProgress size={24} />
            </Box>
          }
        >
          <LatLngPickerMap value={value} onChange={onChange} disabled={disabled} />
        </Suspense>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Map unavailable — set a Google Maps API key to pick coordinates on a map.
        </Typography>
      )}
    </Stack>
  );
}
